import { Box } from '@chakra-ui/react';
import type { Location } from 'history';
import type { ReactNode } from 'react';
import type { match as RouteMatch } from 'react-router';
import { NavLink } from 'react-router-dom';

export type RouteSectionNavItem = {
  /** Stable key for list rendering */
  id: string;
  /** Router `to` value (include `location.search` when routes should preserve query) */
  to: string;
  /** Whether this item is the current section */
  isActive: (pathname: string) => boolean;
  /** Label content (plain text or `<T />`, etc.) */
  label: ReactNode;
};

export type RouteSectionNavProps = {
  /** Passed to `nav` as `aria-label` (translate at the call site) */
  ariaLabel: string;
  items: RouteSectionNavItem[];
  marginBottom?: number | string;
};

export const RouteSectionNav = ({
  ariaLabel,
  items,
  marginBottom = 10,
}: RouteSectionNavProps) => (
  <Box as='nav' marginBottom={marginBottom} aria-label={ariaLabel}>
    <Box
      as='ul'
      display='flex'
      flexWrap='wrap'
      alignItems='center'
      listStyleType='none'
      m={0}
      p={0}
      gap={2}
    >
      {items.map((item) => (
        <Box key={item.id} as='li'>
          <Box
            as={NavLink}
            to={item.to}
            isActive={(_match: RouteMatch | null, loc: Location) =>
              item.isActive(loc.pathname)}
            display='inline-flex'
            alignItems='center'
            justifyContent='center'
            borderRadius='full'
            height='40px'
            px={6}
            fontWeight='bold'
            color='blackAlpha.600'
            textDecoration='none'
            _focus={{ outline: 'none' }}
            _focusVisible={{ boxShadow: 'outline' }}
            sx={{
              '&.active': {
                color: 'var(--colors-primary-fg)',
                bg: 'var(--colors-primary-bg)',
              },
            }}
          >
            {item.label}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);
